import { getCorporate } from "../../../../app/api/corporate";
import { BaseAction } from "../../../../core/actions/BaseAction";
import {
    getRecoilStateValue,
    updateRecoilState,
} from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse } from "../../../../Orchestrator/Types/Index";
import { ICorporateResponse } from "../../../../Orchestrator/Types/Interface/ICorporate";
import { IUserResponse } from "../../../../Orchestrator/Types/Interface/IUser";

/**
 * Loads the company behind the logged-in customer's corporateId.
 *
 * Shaped after GetUserDetailsAction: all five My Company Account panels read
 * corporateDataAtom, so the fetch is deduplicated and cached for the session
 * rather than repeated once per panel. Mutations call invalidate().
 */
class GetCorporateAction extends BaseAction<ICorporateResponse, void> {
    protected override deduplicate = true;

    private _cachedCorporate: ICorporateResponse | null = null;
    /** The company the cache belongs to, so it can never answer for another. */
    private _cachedForId: number | null = null;

    invalidate(): void {
        this._cachedCorporate = null;
        this._cachedForId = null;
    }

    /**
     * A failed fetch only counts as an error when we have nothing to show.
     *
     * When a company is already loaded — e.g. the reconciling re-read after a
     * save — a transient failure must leave it in place. Blanking it here would
     * fail the admin check and eject the admin from the section, which is the
     * bug this action already had once via the PUT response.
     */
    private reportFailure(): void {
        const existing = getRecoilStateValue<ICorporateResponse>("corporateDataAtom");
        if (existing) {
            console.warn(
                "[GetCorporateAction] Keeping the company already loaded; the refresh failed."
            );
            return;
        }
        updateRecoilState("corporateLoadErrorAtom", true);
        updateRecoilState("corporateDataAtom", null);
    }

    override async run(): Promise<ICorporateResponse | null> {
        const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
        const corporateId = userData?.corporateId;

        // Not a corporate customer — nothing to load, and not an error.
        if (corporateId == null) {
            updateRecoilState("corporateDataAtom", null);
            return null;
        }

        if (this._cachedCorporate != null && this._cachedForId === corporateId) {
            updateRecoilState("corporateDataAtom", this._cachedCorporate);
            return this._cachedCorporate;
        }

        updateRecoilState("corporateLoadingAtom", true);
        updateRecoilState("corporateLoadErrorAtom", false);

        try {
            const response: IApiResponse<ICorporateResponse> =
                await getCorporate(corporateId);

            if (response.status === "ok" && response.answer) {
                this._cachedCorporate = response.answer;
                this._cachedForId = corporateId;
                updateRecoilState("corporateDataAtom", response.answer);
                return response.answer;
            }

            console.warn("[GetCorporateAction] Failed to fetch the company.");
            this.reportFailure();
            return null;
        } catch (error: any) {
            console.error("[GetCorporateAction] Error fetching the company:", error);
            this.reportFailure();
            return null;
        } finally {
            updateRecoilState("corporateLoadingAtom", false);
        }
    }
}

export const getCorporateAction = new GetCorporateAction();
